import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { Globe, Tag, Trash2, Plus, Users, User, ShieldCheck, ShieldOff, Search } from "lucide-react";
import { useAccessGroups, GroupSelect, LevelSelect, GateToggles, LEVELS } from "@/components/bi/AccessGrants";

const TIER_LABEL = {
  super_admin: "Super admin", owner: "Owner", tenant_admin: "Workspace admin", direct_deny: "Explicit No Access",
  direct: "Direct user grant", group: "Group grant", category: "Category grant", all: "All-dashboards grant", none: "No grant",
};

function GrantForm({ groups, onSaved }) {
  const [ptype, setPtype] = useState("group");
  const [gid, setGid] = useState("");
  const [email, setEmail] = useState("");
  const [scope, setScope] = useState("all");
  const [category, setCategory] = useState("");
  const [level, setLevel] = useState("viewer");
  const [gates, setGates] = useState({});
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/access/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {}); }, []);
  const save = async () => {
    const body = { principal_type: ptype, scope, level, gates, category: scope === "category" ? category.trim() : null };
    if (ptype === "group") body.principal_id = gid; else body.email = email.trim();
    if ((ptype === "group" && !gid) || (ptype === "user" && !email.trim())) { toast.error("Pick who the grant applies to"); return; }
    if (scope === "category" && !category.trim()) { toast.error("Enter a category"); return; }
    try { const { data } = await api.post("/access/grants", body); toast.success("Grant saved"); onSaved(data); setEmail(""); setGid(""); setCategory(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="grant-form" className="glass-card p-4 space-y-3">
      <p className="text-sm font-medium text-[color:var(--ink)]">Add a workspace-wide grant</p>
      <div className="grid grid-cols-1 md:grid-cols-[130px_minmax(0,1fr)_150px_minmax(0,1fr)_120px] gap-2 items-center">
        <Select value={ptype} onValueChange={(v) => { setPtype(v); setGid(""); setEmail(""); }}>
          <SelectTrigger data-testid="grant-ptype" className="h-8 text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="group">Group</SelectItem><SelectItem value="user">User</SelectItem></SelectContent>
        </Select>
        {ptype === "group"
          ? <GroupSelect value={gid} onChange={setGid} groups={groups} testid="grant-group" />
          : <Input data-testid="grant-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@email.com" className="h-8 text-xs bg-[color:var(--surface)]" />}
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="grant-scope" className="h-8 text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All dashboards</SelectItem><SelectItem value="category">Dashboards in category</SelectItem></SelectContent>
        </Select>
        {scope === "category"
          ? <><Input data-testid="grant-category" list="grant-category-options" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category name" className="h-8 text-xs bg-[color:var(--surface)]" maxLength={60} />
            <datalist id="grant-category-options">{cats.map((c) => <option key={c} value={c} />)}</datalist></>
          : <span className="text-xs text-[color:var(--ink-3)] px-1">Every dashboard in this workspace</span>}
        <LevelSelect value={level} onChange={setLevel} testid="grant-level" className="w-full" />
      </div>
      <div className="flex items-center justify-between gap-2">
        <GateToggles gates={gates} level={level} onChange={setGates} testid="grant-gate" />
        <Button data-testid="grant-save" size="sm" className="h-8 bg-[color:var(--brand-ocean)] text-white" onClick={save}><Plus className="w-4 h-4 mr-1" />Add grant</Button>
      </div>
    </div>
  );
}

function EffectiveAccessTool({ users }) {
  const [dashes, setDashes] = useState([]);
  const [uid, setUid] = useState("");
  const [did, setDid] = useState("");
  const [res, setRes] = useState(null);
  useEffect(() => { api.get("/dashboards").then(({ data }) => setDashes(data)).catch(() => {}); }, []);
  const run = async () => {
    if (!uid || !did) { toast.error("Pick a user and a dashboard"); return; }
    try { const { data } = await api.get("/access/effective", { params: { dashboard_id: did, user_id: uid } }); setRes(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="effective-access-tool" className="glass-card p-4 space-y-3">
      <p className="text-sm font-medium text-[color:var(--ink)] flex items-center gap-1.5"><Search className="w-4 h-4" />Check effective access</p>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2">
        <Select value={uid} onValueChange={setUid}>
          <SelectTrigger data-testid="effective-user" className="h-8 text-xs bg-[color:var(--surface)]"><SelectValue placeholder="User…" /></SelectTrigger>
          <SelectContent>{(users || []).map((u) => <SelectItem key={u.id} value={u.id} data-testid={`effective-user-opt-${u.id}`}>{u.email}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={did} onValueChange={setDid}>
          <SelectTrigger data-testid="effective-dashboard" className="h-8 text-xs bg-[color:var(--surface)]"><SelectValue placeholder="Dashboard…" /></SelectTrigger>
          <SelectContent>{dashes.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`effective-dash-opt-${d.id}`}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="effective-run" size="sm" variant="outline" className="h-8" onClick={run}>Check</Button>
      </div>
      {res && (
        <div data-testid="effective-result" className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 text-xs space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            {res.can_view_data ? <ShieldCheck className="w-4 h-4 text-[color:var(--success)]" /> : <ShieldOff className="w-4 h-4 text-[color:var(--danger)]" />}
            <b className="text-[color:var(--ink)]">{res.user.email}</b> on <b className="text-[color:var(--ink)]">{res.dashboard.name}</b>
            <Badge variant="outline" data-testid="effective-tier">{TIER_LABEL[res.tier] || res.tier}</Badge>
            <Badge variant="outline" data-testid="effective-level" className="capitalize">{res.level}</Badge>
            {res.via && <span className="text-[color:var(--ink-3)]">via {res.via}</span>}
          </div>
          <div><span className="text-[color:var(--ink-3)]">Actions:</span> {res.actions.length ? res.actions.join(", ") : "none"}</div>
          <div className="space-y-0.5">
            <span className="text-[color:var(--ink-3)]">Datasets (strict rule — all must be accessible):</span>
            {res.datasets.map((d) => <div key={d.dataset_id} data-testid="effective-dataset-row" className="flex items-center gap-1.5">{d.access ? <ShieldCheck className="w-3 h-3 text-[color:var(--success)]" /> : <ShieldOff className="w-3 h-3 text-[color:var(--danger)]" />}{d.name}</div>)}
            {!res.datasets.length && <div className="text-[color:var(--ink-3)]">No datasets on this dashboard</div>}
          </div>
          <div data-testid="effective-verdict" className={res.can_view_data ? "text-[color:var(--success)] font-medium" : "text-[color:var(--danger)] font-medium"}>
            {res.can_view_data ? "Can open the dashboard and see its data" : (res.actions.includes("view") ? "Can open the dashboard, but some widgets will show “No access to this data source”" : "Cannot open this dashboard")}
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminPermissions({ users }) {
  const { all } = useAccessGroups();
  const [items, setItems] = useState(null);
  const load = () => api.get("/access/grants").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const remove = async (g) => {
    try { await api.delete(`/access/grants/${g.id}`); toast.success("Grant removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const update = async (g, patch) => {
    try { await api.post("/access/grants", { principal_type: g.principal_type, principal_id: g.principal_id, scope: g.scope, category: g.category, level: g.level, gates: g.gates, ...patch }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="admin-permissions">
      <div className="glass-card p-4 text-xs text-[color:var(--ink-3)] space-y-1">
        <p className="text-sm font-medium text-[color:var(--ink)]">How access is decided</p>
        <p>Super admin → owner → explicit <b>No access</b> on the dashboard → direct user grant → group grant on the dashboard → <b>category grant</b> → <b>all-dashboards grant</b> → none. Within one step the highest level wins. Viewing data additionally requires access to every dataset the dashboard uses.</p>
      </div>
      <GrantForm groups={all} onSaved={load} />
      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--surface-2)]"><tr>
            <th className="text-left px-4 py-2.5 font-medium text-[color:var(--ink-2)]">Who</th>
            <th className="text-left px-4 py-2.5 font-medium text-[color:var(--ink-2)]">Scope</th>
            <th className="text-left px-4 py-2.5 font-medium text-[color:var(--ink-2)]">Level</th>
            <th className="text-left px-4 py-2.5 font-medium text-[color:var(--ink-2)]">Gates</th>
            <th className="px-4 py-2.5" />
          </tr></thead>
          <tbody>
            {(items || []).map((g) => (
              <tr key={g.id} data-testid="grant-row" className="border-t border-[color:var(--glass-border)]">
                <td className="px-4 py-2 text-[color:var(--ink)]"><span className="flex items-center gap-1.5">{g.principal_type === "group" ? <Users className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" /> : <User className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />}{g.principal_name || g.principal_id}</span></td>
                <td className="px-4 py-2 text-[color:var(--ink-2)]"><span className="flex items-center gap-1.5">{g.scope === "all" ? <><Globe className="w-3.5 h-3.5" />All dashboards</> : <><Tag className="w-3.5 h-3.5" />{g.category}</>}</span></td>
                <td className="px-4 py-2"><LevelSelect value={g.level} onChange={(v) => update(g, { level: v })} testid={`grant-row-level-${g.id}`} /></td>
                <td className="px-4 py-2"><GateToggles gates={g.gates} level={g.level} onChange={(gates) => update(g, { gates })} testid={`grant-row-gate-${g.id}`} /></td>
                <td className="px-4 py-2 text-right"><Button data-testid={`grant-row-remove-${g.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0 text-[color:var(--danger)]" onClick={() => remove(g)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
            {items && !items.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-[color:var(--ink-3)]">No workspace-wide grants yet. Dashboard owners can still grant users and groups per dashboard.</td></tr>}
          </tbody>
        </table>
      </div>
      <EffectiveAccessTool users={users} />
      <p className="text-[11px] text-[color:var(--ink-3)]">Levels: {LEVELS.map((l) => l.label).join(" · ")}. Every permission change and denied access attempt is recorded under Audit Logs → Access.</p>
    </div>
  );
}
